import { useMutation, useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { ProductAnnouncementAction, ProductAnnouncementKey } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useProductAnnouncementCoordinator } from "providers/ProductAnnouncementCoordinator";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

const PRODUCT_ANNOUNCEMENT_STATUS = gql(`
  query ProductAnnouncementStatus($key: ProductAnnouncementKey!) {
    productAnnouncementStatus(key: $key) {
      shouldShow
    }
  }
`);

const RECORD_PRODUCT_ANNOUNCEMENT_EVENT = gql(`
  mutation RecordProductAnnouncementEvent($input: RecordProductAnnouncementEventInput!) {
    recordProductAnnouncementEvent(input: $input) {
      ok
    }
  }
`);

export type ProductAnnouncementPhase = "idle" | "modal" | "tour";

// `isManualReopen` gates the "don't show again" checkbox, which doesn't make sense on a manual reopen.
type Surface = {
  phase: ProductAnnouncementPhase;
  tourStep: number | null;
  isManualReopen: boolean;
};

const CLOSED: Surface = { phase: "idle", tourStep: null, isManualReopen: false };

type UseProductAnnouncementOptions = {
  /** Skip the status query — for a client-side gate the server also enforces, so ineligible users never issue the request. */
  skip?: boolean;
  /** Which surface opens when the server says shouldShow — tour-only announcements have no modal, they fire on arrival. */
  initialSurface?: "modal" | "tour";
};

export type UseProductAnnouncementResult = {
  phase: ProductAnnouncementPhase;
  tourStep: number | null;
  /** True when the open surface came from `reopenModal`/`reopenTour` rather than the automatic one-time show. */
  isManualReopen: boolean;
  shouldShow: boolean;
  /** "Don't show me this again" checkbox state — shared across the modal and whatever else can exit this announcement (e.g. a tour), so a tour dismiss also respects it. Resets to false on any close. */
  dontShowAgain: boolean;
  setDontShowAgain: (checked: boolean) => void;
  dismissModal: () => void;
  /** Closes the modal without recording a terminal action, so it shows again next time — for a "don't show this again" checkbox flow. */
  softDismissModal: () => void;
  clickPrimaryCta: () => void;
  clickSecondaryCta: () => void;
  startTour: () => void;
  completeTour: () => void;
  /** Terminal unless `dontShowAgain` is false, in which case this comes back next time — same choice the modal's checkbox controls. */
  dismissTour: () => void;
  viewTourStep: (step: number) => void;
  /** Reopens the modal on demand, bypassing the one-time server gate — for a "show me again" affordance. */
  reopenModal: () => void;
  /** Reopens the tour on demand, bypassing the one-time server gate — for a "show me again" affordance. */
  reopenTour: () => void;
};

export type ShouldShowResult = {
  shouldShow: boolean;
  /** True while a fresh answer is in flight — `network-only` still surfaces the previous result meanwhile. */
  loading: boolean;
  /** Re-asks the server. Needed because recording a terminal action doesn't touch this query's cache, so `shouldShow` would otherwise stay true for the life of the mount. */
  refetch: () => void;
};

/** Reads an announcement's status without recording an impression, so a caller can get the page ready before the one showing is spent. */
export function useProductAnnouncementShouldShow(
  key: ProductAnnouncementKey,
  { skip = false }: { skip?: boolean } = {}
): ShouldShowResult {
  const { tenantId } = useTenant();

  const { data, error, loading, refetch } = useQuery(PRODUCT_ANNOUNCEMENT_STATUS, {
    variables: { key },
    skip: skip || !tenantId,
    fetchPolicy: "network-only",
    // Without this, `loading` stays false during a refetch, so a caller (e.g. the tour's relocation guard) can act on the stale previous answer.
    notifyOnNetworkStatusChange: true,
    errorPolicy: "ignore",
    context: { tenantIdOverride: tenantId },
  });

  return {
    shouldShow: !error && tenantId != null && data?.productAnnouncementStatus.shouldShow === true,
    loading,
    refetch: () => void refetch(),
  };
}

type Coordinator = ReturnType<typeof useProductAnnouncementCoordinator>;

/** Records an action, then re-asks the server, since the mutation doesn't touch the status query's cache. Sequenced after the write — refetching first reads back the pre-action answer. */
function useRecordAndResync(
  record: (action: ProductAnnouncementAction) => Promise<unknown>,
  refetchShouldShow: () => void
) {
  return useCallback(
    (action: ProductAnnouncementAction) => {
      void record(action).then(refetchShouldShow);
    },
    [record, refetchShouldShow]
  );
}

/** Records an optional action, then closes and releases the floor — the shared shape of every exit callback. Also resets `dontShowAgain`, so a checked box doesn't survive to the next reopen. */
function useCloseSurface(
  record: (action: ProductAnnouncementAction) => void,
  coordinator: Coordinator,
  key: ProductAnnouncementKey,
  setSurface: Dispatch<SetStateAction<Surface>>,
  setDontShowAgain: Dispatch<SetStateAction<boolean>>
) {
  return useCallback(
    (action?: ProductAnnouncementAction) => {
      if (action) {
        record(action);
      }
      setSurface(CLOSED);
      setDontShowAgain(false);
      coordinator?.release(key);
    },
    [record, coordinator, key, setSurface, setDontShowAgain]
  );
}

/** Records an action and opens a surface, guarded by the coordinator's floor; a blocked attempt calls `onBlocked` instead of silently no-op-ing. */
function useOpenSurface(
  record: (action: ProductAnnouncementAction) => Promise<unknown>,
  coordinator: Coordinator,
  key: ProductAnnouncementKey,
  setSurface: Dispatch<SetStateAction<Surface>>,
  onBlocked: (action: ProductAnnouncementAction, surface: Surface) => void
) {
  return useCallback(
    (action: ProductAnnouncementAction, surface: Surface) => {
      if (coordinator && !coordinator.acquire(key)) {
        onBlocked(action, surface);
        return;
      }
      void record(action);
      setSurface(surface);
    },
    [record, coordinator, key, setSurface, onBlocked]
  );
}

/** Wraps `useOpenSurface` with a retry once the floor frees up — `coordinator` gets a new identity whenever `openKey` changes, so this reruns exactly then. */
function useReopenSurface(
  record: (action: ProductAnnouncementAction) => Promise<unknown>,
  coordinator: Coordinator,
  key: ProductAnnouncementKey,
  setSurface: Dispatch<SetStateAction<Surface>>
) {
  const [pendingReopen, setPendingReopen] = useState<{
    action: ProductAnnouncementAction;
    surface: Surface;
  } | null>(null);

  const openSurface = useOpenSurface(record, coordinator, key, setSurface, (action, surface) =>
    setPendingReopen({ action, surface })
  );

  useEffect(() => {
    if (!pendingReopen) {
      return;
    }
    if (coordinator && !coordinator.acquire(key)) {
      return;
    }
    void record(pendingReopen.action);
    setSurface(pendingReopen.surface);
    setPendingReopen(null);
  }, [pendingReopen, coordinator, key, record, setSurface]);

  return openSurface;
}

/** Drives a one-time in-app announcement: asks the server whether to show, opens the surface, and reports every lifecycle action back. */
export function useProductAnnouncement(
  key: ProductAnnouncementKey,
  { skip = false, initialSurface = "modal" }: UseProductAnnouncementOptions = {}
): UseProductAnnouncementResult {
  const { tenantId } = useTenant();
  const { shouldShow, refetch: refetchShouldShow } = useProductAnnouncementShouldShow(key, {
    skip,
  });
  const coordinator = useProductAnnouncementCoordinator();

  const [recordEvent] = useMutation(RECORD_PRODUCT_ANNOUNCEMENT_EVENT, {
    errorPolicy: "ignore",
    context: { tenantIdOverride: tenantId },
  });
  const hasRecordedShownRef = useRef(false);
  // Phase and step move together — a tour step only means anything while the
  // tour is open — so they are one piece of state, not two.
  const [surface, setSurface] = useState<Surface>(CLOSED);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const record = useCallback(
    (action: ProductAnnouncementAction, step?: number) =>
      recordEvent({
        variables: {
          input: { key, action, ...(step === undefined ? {} : { tourStep: step }) },
        },
      }),
    [recordEvent, key]
  );

  // The server's answer opens the surface, so this belongs in an effect — re-attempts once the coordinator's floor frees up.
  useEffect(() => {
    if (!shouldShow || hasRecordedShownRef.current) {
      return;
    }
    if (coordinator && !coordinator.acquire(key)) {
      return;
    }
    hasRecordedShownRef.current = true;
    void record(ProductAnnouncementAction.Shown);
    setSurface({
      phase: initialSurface,
      tourStep: initialSurface === "tour" ? 1 : null,
      isManualReopen: false,
    });
  }, [shouldShow, record, initialSurface, coordinator, key]);

  // Safety net only: releases the floor on unmount if still held, so a stuck surface can't block every other announcement.
  const coordinatorRef = useRef(coordinator);
  coordinatorRef.current = coordinator;
  useEffect(() => {
    return () => coordinatorRef.current?.release(key);
  }, [key]);

  // Only the exit paths resync — a terminal action is what changes the answer.
  const recordAndResync = useRecordAndResync(record, refetchShouldShow);

  const closeSurface = useCloseSurface(
    recordAndResync,
    coordinator,
    key,
    setSurface,
    setDontShowAgain
  );
  const openSurface = useReopenSurface(record, coordinator, key, setSurface);

  const viewTourStep = useCallback(
    (step: number) => {
      setSurface((current) => ({
        phase: "tour",
        tourStep: step,
        isManualReopen: current.isManualReopen,
      }));
      void record(ProductAnnouncementAction.TourStepViewed, step);
    },
    [record]
  );

  return {
    phase: surface.phase,
    tourStep: surface.tourStep,
    isManualReopen: surface.isManualReopen,
    shouldShow,
    dontShowAgain,
    setDontShowAgain,
    dismissModal: () => closeSurface(ProductAnnouncementAction.Dismissed),
    softDismissModal: () => closeSurface(),
    clickPrimaryCta: () => closeSurface(ProductAnnouncementAction.PrimaryCtaClicked),
    // Analytics only — the surface stays open so the user can come back to it.
    clickSecondaryCta: () => void record(ProductAnnouncementAction.SecondaryCtaClicked),
    startTour: () => {
      void record(ProductAnnouncementAction.TourStarted);
      setSurface({ phase: "tour", tourStep: 1, isManualReopen: false });
    },
    completeTour: () => closeSurface(ProductAnnouncementAction.TourCompleted),
    dismissTour: () =>
      closeSurface(dontShowAgain ? ProductAnnouncementAction.TourDismissed : undefined),
    viewTourStep,
    // Bypasses `shouldShow` and never touches `finalAction` — a manual reopen shouldn't affect the automatic gate.
    reopenModal: () =>
      openSurface(ProductAnnouncementAction.Shown, {
        phase: "modal",
        tourStep: null,
        isManualReopen: true,
      }),
    reopenTour: () =>
      openSurface(ProductAnnouncementAction.TourStarted, {
        phase: "tour",
        tourStep: 1,
        isManualReopen: true,
      }),
  };
}
